// Admin queue: review reports awaiting moderation.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { adminResolveReviewReport } from "@/lib/reviews.functions";
import { Loader2, ShieldAlert, Star, Flag } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/reviews")({
  head: () => ({ meta: [{ title: "Review reports — Admin — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: AdminReviews,
});

type Report = {
  id: string;
  review_id: string;
  reason: string;
  details: string | null;
  status: string;
  created_at: string;
  reporter: { full_name: string | null; email: string | null } | null;
  review: {
    id: string; rating: number; comment: string | null; reviewer_type: string;
    booking_id: string; response_text: string | null;
    report_count: number; hidden_by_admin: boolean;
    reviewer: { full_name: string | null } | null;
    reviewee: { full_name: string | null } | null;
  } | null;
};

function AdminReviews() {
  const navigate = useNavigate();
  const resolve = useServerFn(adminResolveReviewReport);
  const [rows, setRows] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("review_reports")
      .select("id, review_id, reason, details, status, created_at, reporter:profiles!review_reports_reporter_id_fkey(full_name,email), review:reviews!review_reports_review_id_fkey(id, rating, comment, reviewer_type, booking_id, response_text, report_count, hidden_by_admin, reviewer:profiles!reviews_reviewer_id_fkey(full_name), reviewee:profiles!reviews_reviewee_id_fkey(full_name))")
      .eq("status", "pending")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) toast.error(error.message);
    setRows((data ?? []) as unknown as Report[]);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) { navigate({ to: "/auth", search: { redirect: "/admin/reviews" } }); return; }
      const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: u.user.id, _role: "admin" });
      if (!isAdmin) { toast.error("Admin only"); navigate({ to: "/" }); return; }
      await load();
    })();
  }, [navigate]);

  async function decide(reviewId: string, decision: "uphold" | "dismiss") {
    setBusy(reviewId);
    try {
      await resolve({ data: { reviewId, decision } });
      toast.success(decision === "uphold" ? "Review hidden" : "Report dismissed");
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed"); }
    finally { setBusy(null); }
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6">
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Review reports</h1>
        <p className="mt-1 text-sm text-muted-foreground">Moderate flagged reviews. Upholding hides the review and flags it; dismissing clears all reports.</p>

        {loading ? (
          <div className="mt-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
            No pending reports — great job keeping the marketplace clean.
          </div>
        ) : (
          <ul className="mt-6 space-y-4">
            {rows.map((r) => (
              <li key={r.id} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <header className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Flag className="h-4 w-4 text-rose-500" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">{r.reason}</span>
                    {r.review && r.review.report_count > 1 && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">{r.review.report_count} reports</span>}
                    {r.review?.hidden_by_admin && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Auto-hidden</span>}
                  </div>
                  <time className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-GB")}</time>
                </header>
                {r.details && <p className="mt-2 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">Reporter note: {r.details}</p>}
                {r.review && (
                  <div className="mt-3 rounded-xl border border-border p-4">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="rounded-full border border-border bg-muted px-2 py-0.5 font-semibold uppercase tracking-wide">{r.review.reviewer_type}</span>
                      <div className="flex">
                        {[1,2,3,4,5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= r.review!.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />)}
                      </div>
                      <span>{r.review.reviewer?.full_name ?? "—"} → {r.review.reviewee?.full_name ?? "—"}</span>
                    </div>
                    {r.review.comment && <p className="mt-2 whitespace-pre-wrap text-sm">{r.review.comment}</p>}
                    {r.review.response_text && (
                      <p className="mt-2 rounded-lg bg-muted/40 p-2 text-xs"><strong>Response:</strong> {r.review.response_text}</p>
                    )}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="destructive" disabled={busy === r.review_id} onClick={() => decide(r.review_id, "uphold")}>
                    <ShieldAlert className="mr-1 h-4 w-4" />Uphold &amp; hide
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === r.review_id} onClick={() => decide(r.review_id, "dismiss")}>
                    Dismiss report
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}
